"use client";

import { useEffect, useCallback, useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Rocket, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";

const HANKO_API_URL = process.env.NEXT_PUBLIC_HANKO_API_URL ?? "";
const DEV_LOGIN = process.env.NEXT_PUBLIC_DEV_LOGIN === "true";

function LoginContent() {
  const { loginWithToken, refreshUser } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") ?? "/deployments";

  const [devPassword, setDevPassword] = useState("");
  const [devError, setDevError] = useState("");
  const [devLoading, setDevLoading] = useState(false);

  const handleSession = useCallback(async (token: string) => {
    const ok = await loginWithToken(token);
    if (ok) router.push(redirect);
  }, [loginWithToken, redirect, router]);

  useEffect(() => {
    if (!HANKO_API_URL) return;
    import("@teamhanko/hanko-elements").then(({ register, Hanko }) => {
      register(HANKO_API_URL).catch(console.error);
      const hanko = new Hanko(HANKO_API_URL);
      return hanko.onSessionCreated(async () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const token = await (hanko.session as any).getValidToken() as string | null;
        if (token) void handleSession(token);
      });
    }).catch(console.error);
  }, [handleSession]);

  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setDevError("");
    setDevLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ devPassword }),
      });
      if (!res.ok) { setDevError("Wrong password."); return; }
      const { token } = await res.json() as { token: string };
      localStorage.setItem("oe_token", token);
      refreshUser();
      router.push(redirect);
    } finally {
      setDevLoading(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 items-center justify-center bg-deck-surface/40 px-4 py-16">
      <div className="w-full max-w-sm space-y-6">

        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex size-10 items-center justify-center rounded-xl bg-linear-to-br from-brand-highlight to-brand-accent text-white shadow-lg shadow-brand-highlight/30">
            <Rocket className="size-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Sign in</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Ocean Enterprise Portal</p>
          </div>
        </div>

        {HANKO_API_URL ? (
          <div className="rounded-2xl border border-border bg-card p-6 shadow-xl shadow-black/[0.06]">
            {/* @ts-expect-error — hanko-auth is a registered custom element */}
            <hanko-auth />
          </div>
        ) : (
          <p className="text-center text-xs text-muted-foreground">
            NEXT_PUBLIC_HANKO_API_URL is not set.
          </p>
        )}

        {/* Dev-only bypass — visible only when NEXT_PUBLIC_DEV_LOGIN=true */}
        {DEV_LOGIN && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-3">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Dev login</p>
            {devError && (
              <div className="flex items-center gap-2 text-xs text-red-700">
                <AlertCircle className="size-3.5 shrink-0" /> {devError}
              </div>
            )}
            <form onSubmit={handleDevLogin} className="flex gap-2">
              <input
                type="password"
                value={devPassword}
                onChange={(e) => setDevPassword(e.target.value)}
                placeholder="OE_DEV_PASSWORD"
                className="flex-1 h-8 rounded-lg border border-amber-300 bg-white px-2 text-xs outline-none focus:ring-2 focus:ring-amber-400"
              />
              <button
                type="submit"
                disabled={devLoading || !devPassword}
                className="h-8 px-3 rounded-lg bg-amber-500 text-white text-xs font-medium disabled:opacity-50"
              >
                {devLoading ? "…" : "Go"}
              </button>
            </form>
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Back to{" "}
          <Link href="/" className="text-brand-highlight hover:underline">
            home
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  );
}
